package com.vamvisions.main.pages.portfolio

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.vamvisions.main.components.Card
import com.vamvisions.main.components.ConnectCard
import com.vamvisions.main.components.ErrorView
import com.vamvisions.main.components.LoadingView
import com.vamvisions.main.components.Section
import com.vamvisions.main.components.SkillList
import com.vamvisions.main.core.presentation.i18n.resolve
import com.vamvisions.main.core.presentation.navigation.Route
import com.vamvisions.main.di.koinViewModel
import com.vamvisions.main.i18n.LocalStrings
import com.vamvisions.main.layout.PageChrome
import com.vamvisions.main.layout.PageLayout
import com.vamvisions.main.navigation.AppLink
import com.vamvisions.main.navigation.ExternalLink
import com.vamvisions.main.portfolio.presentation.common.LinkTarget
import com.vamvisions.main.portfolio.presentation.portfolio.ExperienceUi
import com.vamvisions.main.portfolio.presentation.portfolio.PortfolioAction
import com.vamvisions.main.portfolio.presentation.portfolio.PortfolioState
import com.vamvisions.main.portfolio.presentation.portfolio.PortfolioViewModel
import com.vamvisions.main.theme.REDUCED_MOTION_QUERY
import com.vamvisions.main.theme.rememberReveal
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/** The timeline cards appear one after another, this far apart. */
private const val TIMELINE_STAGGER_MS = 100

@Composable
fun PortfolioRoot(chrome: PageChrome) {
    val viewModel = koinViewModel<PortfolioViewModel>()
    val state by viewModel.state.collectAsState()
    LaunchedEffect(chrome.language) {
        viewModel.onAction(PortfolioAction.LanguageChanged(chrome.language))
    }
    ScrollToHashOnce(state.hasContent && !state.isLoading)
    PageLayout(title = LocalStrings.current.portfolioTitle, chrome = chrome, panel = false) {
        PortfolioScreen(state = state, onAction = viewModel::onAction, onNavigate = chrome.onNavigate)
    }
}

/** The intro and the "Let's connect" card show in every state; the sections only once the content has loaded. */
@Composable
fun PortfolioScreen(state: PortfolioState, onAction: (PortfolioAction) -> Unit, onNavigate: (Route) -> Unit) {
    val strings = LocalStrings.current
    Div(attrs = { classes(PortfolioStyles.page) }) {
        Div(attrs = { classes(PortfolioStyles.intro) }) {
            H1(attrs = { classes(PortfolioStyles.introTitle) }) { Text(strings.portfolioIntroTitle) }
            P(attrs = { classes(PortfolioStyles.introText) }) { Text(strings.portfolioIntroText) }
        }
        val error = state.error
        when {
            error != null -> ErrorView(error.resolve(strings)) { onAction(PortfolioAction.Retry) }
            !state.hasContent && state.isLoading -> LoadingView()
            // While another language loads, the previous content stays and is marked busy.
            else -> Div(attrs = {
                classes(PortfolioStyles.content)
                attr("aria-busy", state.isLoading.toString())
            }) {
                if (state.timeline.isNotEmpty()) {
                    Section("experience", strings.portfolioExperienceTitle, strings.portfolioExperienceEyebrow, attrs = rememberReveal()) {
                        Timeline(state.timeline, onNavigate)
                    }
                }
                if (state.learning.isNotEmpty()) {
                    Section("learning", strings.portfolioLearningTitle, strings.portfolioLearningEyebrow, attrs = rememberReveal()) {
                        Ul(attrs = { classes(PortfolioStyles.learning) }) {
                            state.learning.forEach { entry ->
                                // Each learning card is a Home page anchor, such as #pl-coding.
                                Li(attrs = { classes(PortfolioStyles.learningItem) }) { EntryCard(entry, onNavigate, id = entry.slug) }
                            }
                        }
                    }
                }
                if (state.education.isNotEmpty()) {
                    Section("education", strings.portfolioEducationTitle, strings.portfolioEducationEyebrow, attrs = rememberReveal()) {
                        Ul(attrs = { classes(PortfolioStyles.timeline) }) {
                            state.education.forEach { entry -> Li { EntryCard(entry, onNavigate) } }
                        }
                    }
                }
            }
        }
        ConnectCard(onNavigate)
    }
}

/** Newest first: a dot on the rail for each card, joined by a line that draws downward as the cards appear. */
@Composable
private fun Timeline(entries: List<ExperienceUi>, onNavigate: (Route) -> Unit) {
    Ul(attrs = { classes(PortfolioStyles.timeline) }) {
        entries.forEachIndexed { index, entry ->
            val reveal = rememberReveal(delayMs = index * TIMELINE_STAGGER_MS)
            Li(attrs = {
                classes(PortfolioStyles.timelineRow)
                reveal()
            }) {
                Span(attrs = {
                    classes(PortfolioStyles.rail)
                    attr("aria-hidden", "true")
                }) {
                    Span(attrs = {
                        classes(PortfolioStyles.dot)
                        if (entry.isCurrent) classes(PortfolioStyles.dotCurrent)
                    })
                }
                EntryCard(entry, onNavigate)
            }
        }
    }
}

/**
 * One entry: name (a link when it has one), period, role, highlights, skills, and for the current entry the links
 * to the Projects and Contact pages.
 */
@Composable
private fun EntryCard(entry: ExperienceUi, onNavigate: (Route) -> Unit, id: String? = null) {
    val strings = LocalStrings.current
    Card(glass = true, attrs = {
        classes(PortfolioStyles.entry)
        if (entry.isCurrent) classes(PortfolioStyles.entryCurrent)
        id?.let { id(it) }
    }) {
        Div(attrs = { classes(PortfolioStyles.entryHead) }) {
            H3(attrs = { classes(PortfolioStyles.entryName) }) { EntryName(entry, onNavigate) }
            P(attrs = { classes(PortfolioStyles.entryPeriod) }) { Text(entry.period) }
            P(attrs = { classes(PortfolioStyles.entryRole) }) { Text(entry.role) }
        }
        Highlights(entry.highlights)
        SkillList(ownerId = entry.slug, skills = entry.skills)
        if (entry.isCurrent) {
            Div(attrs = { classes(PortfolioStyles.entryLinks) }) {
                EntryLink(strings.portfolioSeeProjects, Route.Projects, onNavigate)
                EntryLink(strings.portfolioWorkWithMe, Route.Contact, onNavigate)
            }
        }
    }
}

@Composable
private fun EntryName(entry: ExperienceUi, onNavigate: (Route) -> Unit) {
    when (val link = entry.link) {
        is LinkTarget.Internal -> AppLink(link.route, onNavigate, attrs = { classes(PortfolioStyles.entryNameLink) }) {
            Text(entry.name)
        }
        is LinkTarget.External -> ExternalLink(link.url, attrs = { classes(PortfolioStyles.entryNameLink) }) {
            Text(entry.name)
        }
        null -> Text(entry.name)
    }
}

/** One highlight is a paragraph, several are a bulleted list. */
@Composable
private fun Highlights(highlights: List<String>) {
    when (highlights.size) {
        0 -> Unit
        1 -> P(attrs = { classes(PortfolioStyles.entryBody) }) { Text(highlights.single()) }
        else -> Ul(attrs = { classes(PortfolioStyles.entryList) }) {
            highlights.forEach { Li { Text(it) } }
        }
    }
}

/** A text link with a trailing arrow, hidden from assistive technology. */
@Composable
private fun EntryLink(label: String, route: Route, onNavigate: (Route) -> Unit) {
    AppLink(route, onNavigate, attrs = { classes(PortfolioStyles.entryLink) }) {
        Text(label)
        Span(attrs = { attr("aria-hidden", "true") }) { Text(" →") }
    }
}

/**
 * Once the content has first [loaded], scrolls to the element named by the URL hash, after the next frame so the cards
 * have their final size. It runs once per page visit, so a language reload doesn't pull the reader back.
 */
@Composable
private fun ScrollToHashOnce(loaded: Boolean) {
    var done by remember { mutableStateOf(false) }
    LaunchedEffect(loaded) {
        if (!loaded || done) return@LaunchedEffect
        done = true
        val id = window.location.hash.removePrefix("#").takeIf { it.isNotEmpty() } ?: return@LaunchedEffect
        window.requestAnimationFrame {
            val options: dynamic = js("({ block: 'start' })")
            options.behavior = if (window.matchMedia(REDUCED_MOTION_QUERY).matches) "auto" else "smooth"
            document.getElementById(id)?.asDynamic()?.scrollIntoView(options)
        }
    }
}
